import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Building2, 
  Plus, 
  Eye, 
  Edit, 
  User, 
  Calendar,
  Search,
  Filter
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { chantierService } from '../../services/chantierService';
import { Chantier } from '../../types';

function ChantierList() {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const [chantiers, setChantiers] = useState<Chantier[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    chefEquipeId: user?.role === 'responsable_equipe' ? user.id : ''
  });

  useEffect(() => {
    loadChantiers();
  }, [filters]);

  const loadChantiers = async () => {
    try {
      setLoading(true);
      const data = await chantierService.getChantiers({
        status: filters.status || undefined,
        chefEquipeId: filters.chefEquipeId || undefined
      });
      
      // Filtrer par recherche
      let filtered = data;
      if (filters.search) {
        const search = filters.search.toLowerCase();
        filtered = data.filter(c => 
          c.code.toLowerCase().includes(search) ||
          c.name.toLowerCase().includes(search)
        );
      }
      
      // Filtrer selon les permissions
      if (user) {
        filtered = filtered.filter(c => 
          chantierService.canUserAccessChantier(user.id, user.role, c)
        );
      }
      
      setChantiers(filtered);
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur de chargement',
        message: 'Impossible de charger les chantiers',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const canManageChantiers = user?.role === 'admin' || user?.role === 'responsable_depot' || user?.role === 'responsable_bureau';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card rounded-xl card-hover">
        <div className="px-6 py-6 sm:p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-700 rounded-lg shadow-lg">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900">Chantiers</h1>
            </div>
            
            {canManageChantiers && (
              <Link
                to="/chantiers/new"
                className="btn-primary px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200 flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Nouveau chantier</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="glass-card rounded-xl">
        <div className="px-6 py-5 sm:px-8">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher (code, nom)..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white/80"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white/80"
            >
              <option value="">Tous les statuts</option>
              <option value="en_cours">En cours</option>
              <option value="suspendu">Suspendu</option>
              <option value="termine">Terminé</option>
              <option value="annule">Annulé</option>
            </select>

            {/* Chef d'équipe Filter (only for admins) */}
            {(user?.role === 'admin' || user?.role === 'responsable_bureau' || user?.role === 'responsable_depot') && (
              <select
                value={filters.chefEquipeId}
                onChange={(e) => setFilters({ ...filters, chefEquipeId: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white/80"
              >
                <option value="">Tous les chefs d'équipe</option>
                <option value="3">Thomas A.</option>
                <option value="4">Luc T.</option>
              </select>
            )}
          </div>
        </div>
      </div>

      {/* Chantiers List */}
      <div className="glass-card rounded-xl">
        <div className="px-6 py-5 sm:px-8 border-b border-gray-200/50">
          <h3 className="text-lg font-medium text-gray-900">
            Chantiers ({chantiers.length})
          </h3>
        </div>

        {chantiers.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun chantier</h3>
            <p className="mt-1 text-sm text-gray-500">
              Aucun chantier ne correspond aux critères de recherche
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Chantier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Chef d'équipe
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dates
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white/50 divide-y divide-gray-200">
                {chantiers.map((chantier) => (
                  <tr key={chantier.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {chantier.code}
                        </div>
                        <div className="text-sm text-gray-500">
                          {chantier.name}
                        </div>
                        {chantier.address && (
                          <div className="text-xs text-gray-400 mt-1">
                            {chantier.address}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">
                          {chantier.chefEquipeId === '3' ? 'Thomas A.' : 'Luc T.'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        chantierService.getStatusColor(chantier.status)
                      }`}>
                        {chantierService.getStatusLabel(chantier.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <div>{new Date(chantier.startDate).toLocaleDateString('fr-FR')}</div>
                          {chantier.endDate && (
                            <div className="text-xs text-gray-500">
                              → {new Date(chantier.endDate).toLocaleDateString('fr-FR')}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <Link
                          to={`/chantiers/${chantier.id}`}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="Voir les détails"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                        
                        {canManageChantiers && (
                          <Link
                            to={`/chantiers/${chantier.id}/edit`}
                            className="text-gray-600 hover:text-gray-900 p-1 rounded"
                            title="Modifier"
                          >
                            <Edit className="h-4 w-4" />
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default ChantierList;